import { useEffect } from "react";
import { App, Button, Card, Col, Form, Input, Row, Space, Switch, Typography } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { HomeBanner, HomeFeature } from "@glow-price/shared";

import { siteSettingsApi } from "../api/resources";
import { ImageUpload } from "../components/ImageUpload";

/**
 * 编辑精选 / 标签管理。
 *
 * 两个写口都通过通用的 site_settings 端点读写：
 *   - key `home_feature`  —— 首页"编辑精选"大图卡
 *   - key `home_banners`  —— 首页顶部 3 张轮播 Banner（"今日好价"/"近期最低"/"大牌精选" 等）
 */
const BANNER_VARIANTS = [
  { name: "酒红", bg: "linear-gradient(135deg, #4a1c2a 0%, #6d2839 100%)" },
  { name: "炭墨", bg: "linear-gradient(135deg, #2a2520 0%, #4a3f33 100%)" },
  { name: "酒红→金", bg: "linear-gradient(135deg, #6d2839 0%, #b8956b 100%)" },
];

type BannersForm = { items: HomeBanner[] };

export function EditorialTagsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [featureForm] = Form.useForm<HomeFeature>();
  const [bannerForm] = Form.useForm<BannersForm>();

  const featureQuery = useQuery({
    queryKey: ["site-settings", "home_feature"],
    queryFn: () => siteSettingsApi.get("home_feature"),
  });
  const bannersQuery = useQuery({
    queryKey: ["site-settings", "home_banners"],
    queryFn: () => siteSettingsApi.get("home_banners"),
  });

  const featureValue = (featureQuery.data?.value ?? null) as HomeFeature | null;
  const bannerItems = ((bannersQuery.data?.value as { items?: HomeBanner[] } | null)?.items ??
    []) as HomeBanner[];

  useEffect(() => {
    if (featureValue) featureForm.setFieldsValue(featureValue);
  }, [featureValue, featureForm]);
  useEffect(() => {
    if (bannersQuery.data) bannerForm.setFieldsValue({ items: bannerItems });
  }, [bannersQuery.data, bannerItems, bannerForm]);

  const featureMutation = useMutation({
    mutationFn: (values: HomeFeature) => siteSettingsApi.put("home_feature", values),
    onSuccess: () => {
      message.success("编辑精选已保存，下次进入首页生效");
      qc.invalidateQueries({ queryKey: ["site-settings", "home_feature"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const bannersMutation = useMutation({
    mutationFn: (items: HomeBanner[]) => siteSettingsApi.put("home_banners", { items }),
    onSuccess: () => {
      message.success("Banner 已保存");
      qc.invalidateQueries({ queryKey: ["site-settings", "home_banners"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        编辑精选与标签
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
        管理首页的"编辑精选"大图卡，以及顶部 3 张 Banner（"今日好价" / "近期最低" / "大牌精选" 等运营卡片）。
      </Typography.Paragraph>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="编辑精选（home_feature）" loading={featureQuery.isLoading}>
            <Form<HomeFeature>
              form={featureForm}
              layout="vertical"
              initialValues={featureValue ?? undefined}
              onFinish={(values) => featureMutation.mutate(values)}
            >
              <Form.Item name="enabled" label="启用" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item
                name="eyebrow"
                label="英文小标 (Eyebrow)"
                rules={[{ required: true, max: 40 }]}
              >
                <Input placeholder="Editor's Pick" />
              </Form.Item>
              <Form.Item name="title" label="标题" rules={[{ required: true, max: 60 }]}>
                <Input placeholder="本周编辑精选" />
              </Form.Item>
              <Form.Item name="body" label="副文案" rules={[{ required: true, max: 160 }]}>
                <Input.TextArea rows={2} placeholder="奢护肌底新品 + 经典香氛特惠" />
              </Form.Item>
              <Form.Item name="cta" label="按钮文案" rules={[{ required: true, max: 20 }]}>
                <Input placeholder="探索精选" />
              </Form.Item>
              <Form.Item
                name="linkTo"
                label="跳转路径"
                rules={[{ required: true, max: 120 }]}
              >
                <Input placeholder="/top-deals" />
              </Form.Item>
              <Form.Item
                name="imageUrl"
                label="背景图"
                rules={[{ required: true, type: "url" }]}
              >
                <ImageUpload folder="home-feature" width={120} height={80} />
              </Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={featureMutation.isPending}
                >
                  保存编辑精选
                </Button>
                <Button
                  onClick={() => featureForm.resetFields()}
                  disabled={featureMutation.isPending}
                >
                  撤销
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title="首页 Banner（home_banners）" loading={bannersQuery.isLoading}>
            <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>
              3 个固定槽位，渐变背景按槽位预设；文案 / 跳转可改。
            </Typography.Paragraph>
            <Form<BannersForm>
              form={bannerForm}
              layout="vertical"
              initialValues={{ items: bannerItems }}
              onFinish={(values) => bannersMutation.mutate(values.items)}
            >
              {[0, 1, 2].map((idx) => (
                <Card
                  key={idx}
                  size="small"
                  style={{ marginBottom: 12 }}
                  title={
                    <span>
                      <span
                        style={{
                          display: "inline-block",
                          width: 12,
                          height: 12,
                          borderRadius: 3,
                          background: BANNER_VARIANTS[idx]!.bg,
                          marginRight: 8,
                          verticalAlign: "middle",
                        }}
                      />
                      槽 #{idx + 1}（{BANNER_VARIANTS[idx]!.name}）
                    </span>
                  }
                >
                  <Form.Item
                    name={["items", idx, "title"]}
                    label="标题"
                    rules={[{ required: true, max: 30 }]}
                  >
                    <Input placeholder="今日好价 / 近期最低 / 大牌精选" />
                  </Form.Item>
                  <Form.Item
                    name={["items", idx, "sub"]}
                    label="副文案"
                    rules={[{ required: true, max: 60 }]}
                  >
                    <Input placeholder="中免日上 · 多渠道实时比价" />
                  </Form.Item>
                  <Form.Item
                    name={["items", idx, "linkTo"]}
                    label="跳转路径"
                    rules={[{ required: true, max: 120 }]}
                  >
                    <Input placeholder="/top-deals" />
                  </Form.Item>
                </Card>
              ))}
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={bannersMutation.isPending}
                >
                  保存 3 张 Banner
                </Button>
                <Button
                  onClick={() => bannerForm.resetFields()}
                  disabled={bannersMutation.isPending}
                >
                  撤销
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
